import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Paper,
  Fade,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css"; // Only default styles
import { postRecord } from "services/services";
import { AuthContext } from "context/Authentication";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UserFilesUpload from "templates/default/components/CommonDialogs/UserFilesUpload.jsx";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
const API_HOME_BANNERS_BY_URL = "banners/getbypageid";
export default function HomeSlider() {
  const [banners, setBanners] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [fullWidthImagesLoaded, setFullWidthImagesLoaded] = useState({});
  const [heroImagesLoaded, setHeroImagesLoaded] = useState({});
  const [compactHeroImagesLoaded, setCompactHeroImagesLoaded] = useState({});
  const navigate = useNavigate();
  const context = useContext(AuthContext);

  // Helper function to handle image loading
  const handleImageLoad = (imageUrl, type) => {
    switch (type) {
      case "fullWidth":
        setFullWidthImagesLoaded((prev) => ({ ...prev, [imageUrl]: true }));
        break;
      case "hero":
        setHeroImagesLoaded((prev) => ({ ...prev, [imageUrl]: true }));
        break;
      case "compactHero":
        setCompactHeroImagesLoaded((prev) => ({ ...prev, [imageUrl]: true }));
        break;
      default:
        break;
    }
  };

  // Helper function to handle CTA button navigation
  const handleCTANavigation = (ctaLink) => {
    const normalizedLink = ctaLink.toLowerCase();

    // Check if the link is Register or Login
    if (normalizedLink === "register" || normalizedLink === "login") {
      // Only navigate to Register/Login if user is not logged in
      if (!context.state.user?.User_Id) {
        navigate(`/${ctaLink}`);
      } else {
        // User is logged in, navigate based on product service type
        if (
          context.state.companyInfo?.Product_Service_Type ===
          "Product - Physical"
        ) {
          navigate("/ProductList");
        } else {
          navigate("/ServiceList");
        }
      }
    } else {
      // For all other links, navigate normally
      navigate("/" + ctaLink.replace(/^\/+/, ""));
    }
  };
  // Fetch banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);

        // Single API call to get banners by page URL
        const bannersResponse = await postRecord(API_HOME_BANNERS_BY_URL, {
          Page_ID: 37,
        });
        // console.log("Banners Response:", bannersResponse);
        if (bannersResponse.status === 200 && bannersResponse.data) {
          const finalBanners = bannersResponse.data;
          setBanners(finalBanners);

          // Simple image format for gallery
          const images = finalBanners.map((banner) => ({
            original: `${process.env.BASEURL}${banner.Banner_Image_File_Path}`,
          }));

          setGalleryImages(images);
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Simple slide change handler
  const handleSlideChange = (currentIndex) => {
    // Animate text transition
    setTextVisible(false);
    setTimeout(() => {
      setCurrentSlide(currentIndex);
      setTextVisible(true);
    }, 600);
  };

  // Custom image renderer to get object-fit: cover behavior
  const renderItem = (item) => {
    const imageUrl = item.original;

    return (
      <Box
        className="image-gallery-image"
        sx={{
          width: "100%",
          height: {
            xs: "300px",
            sm: "400px",
            md: "450px",
            lg: "550px",
            xl: "550px",
          },
          position: "relative",
        }}
      >
        <img
          src={imageUrl}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "8px",
          }}
          onLoad={() => handleImageLoad(imageUrl, "hero")}
        />
        {/* Loading overlay */}
        {!heroImagesLoaded[imageUrl] && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.9)"
                  : "rgba(245, 245, 245, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
              backdropFilter: "blur(4px)",
            }}
          >
            <CircularProgress
              size={35}
              sx={{
                color: (theme) => theme.palette.primary.main,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // Separate banners by type
  const heroBanners = banners.filter((b) => b.Banner_Type === "Hero Banner");
  const fullWidthBanners = banners.filter(
    (b) => b.Banner_Type === "Hero Banner - Full width"
  );
  const compactHeroBanners = banners.filter(
    (b) => b.Banner_Type === "Compact Hero Banner"
  );

  // State for location dropdown
  const [location, setLocation] = useState("");
  const locations = ["New York", "London", "Mumbai", "Tokyo"]; // Example locations

  // Compact Hero Banner UI
  const renderCompactHeroBanner = (banner) => (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: {
          xs: "120px",
          sm: "150px",
          md: "180px",
          lg: "200px",
          xl: "220px",
        },
        mb: 4,
        overflow: "hidden",
        boxShadow: 4,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[100],
      }}
    >
      {/* Banner Image */}
      <img
        src={process.env.BASEURL + banner.Banner_Image_File_Path}
        alt={banner.Image_Heading_Text || "Banner"}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
          filter: "brightness(0.85) saturate(1.1)",
          transition: "filter 0.3s",
        }}
      />
      {/* Overlay for dark/light mode */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0.7) 100%)",
          zIndex: 1,
        }}
      />
      {/* Centered content */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          pb: 2,
        }}
      >
        {/* Banner Text */}
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: 700,
            textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            mb: 0.5,
            textAlign: "center",
            letterSpacing: 1,
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
          }}
        >
          {banner.Image_Heading_Text}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            color: "#fff",
            fontWeight: 400,
            textShadow: "0 1px 4px rgba(0,0,0,0.3)",
            mb: 1,
            textAlign: "center",
            fontSize: { xs: "0.9rem", sm: "1rem" },
          }}
        >
          {banner.Image_Sub_Heading_Text}
        </Typography>
        {/* Search bar and location dropdown */}
        <Box
          sx={{
            width: { xs: "98%", sm: "80%", md: "60%", lg: "50%", xl: "40%" },
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(30,30,30,0.92)"
                : "rgba(255,255,255,0.92)",
            borderRadius: "32px",
            boxShadow: 3,
            px: { xs: 1, sm: 2 },
            py: 1,
            display: "flex",
            alignItems: "center",
            position: "relative",
            mb: 1,
            gap: 1,
          }}
        >
          {/* Upload Prescription Button */}
          <Button
            variant="text"
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              position: "absolute",
              right: 12,
              top: -38,
              color: "secondary.main",
            }}
            onClick={() => setDialogOpen(true)}
          >
            Upload Prescription
          </Button>
          {/* Location Dropdown */}
          <Box sx={{ minWidth: 120, mr: 1 }}>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={{
                border: "none",
                background: "transparent",
                fontWeight: 500,
                fontSize: 16,
                color: "#333",
                outline: "none",
                padding: "8px 12px",
                borderRadius: "16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                appearance: "none",
              }}
            >
              <option value="">Select Location</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </Box>
          {/* Search Bar */}
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search for medicines, products..."
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              fontSize: 16,
              padding: "8px 12px",
              outline: "none",
              color: "#333",
            }}
          />
          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "20px",
              ml: 1,
              px: 3,
              fontWeight: 600,
              boxShadow: 1,
              textTransform: "none",
            }}
          >
            Search
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // Compact Hero Banner UI (for slider)
  const renderCompactHeroBannerItem = (item) => {
    const imageUrl = `${process.env.BASEURL}${item.Banner_Image_File_Path}`;

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: {
            xs: "380px",
            sm: "420px",
            md: "480px",
            lg: "520px",
            xl: "560px",
          },
          overflow: "hidden",
          boxShadow: 4,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
        }}
      >
        {/* Banner Image */}
        <img
          src={imageUrl}
          alt={item.Image_Heading_Text || "Banner"}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
            filter: "brightness(0.85) saturate(1.1)",
            transition: "filter 0.3s",
          }}
          onLoad={() => handleImageLoad(imageUrl, "compactHero")}
        />
        {/* Loading overlay */}
        {!compactHeroImagesLoaded[imageUrl] && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.9)"
                  : "rgba(245, 245, 245, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 15,
              backdropFilter: "blur(4px)",
            }}
          >
            <CircularProgress
              size={35}
              sx={{
                color: (theme) => theme.palette.primary.main,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
              }}
            />
          </Box>
        )}
        {/* Overlay for dark/light mode */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(180deg, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)"
                : "linear-gradient(180deg, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0.7) 100%)",
            zIndex: 1,
          }}
        />
        {/* Enhanced text and CTA section at bottom */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-end",
            pb: { xs: 3, sm: 4, md: 5 }, // More spacing from bottom
            px: { xs: 2, sm: 3, md: 4 }, // Horizontal padding for text
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              mb: { xs: 1, sm: 1.5 },
              textAlign: "center",
              letterSpacing: 1,
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
                md: "1.8rem",
                lg: "2rem",
                xl: "2.2rem",
              },
              lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
            }}
          >
            {item.Image_Heading_Text}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 500,
              textShadow: "0 1px 4px rgba(0,0,0,0.4)",
              mb: { xs: 1, sm: 1.5 },
              textAlign: "center",
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
                md: "1.2rem",
                lg: "1.3rem",
              },
              lineHeight: { xs: 1.3, sm: 1.4 },
            }}
          >
            {item.Image_Sub_Heading_Text}
          </Typography>

          {/* Banner description text */}
          {item.Image_Text && (
            <Typography
              variant="body1"
              sx={{
                color: "#fff",
                fontWeight: 400,
                textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                mb: { xs: 2, sm: 2.5 },
                textAlign: "center",
                fontSize: {
                  xs: "0.85rem",
                  sm: "0.95rem",
                  md: "1rem",
                  lg: "1.1rem",
                },
                lineHeight: { xs: 1.4, sm: 1.5 },
                maxWidth: { xs: "100%", sm: "80%", md: "70%" },
                opacity: 0.95,
              }}
            >
              {item.Image_Text}
            </Typography>
          )}

          {/* CTA Button */}
          {item.Banner_CTA_Text &&
            (() => {
              const ctaLink = item.Banner_CTA_Button_Link || "";
              // Check for external link: starts with http/https, or contains common TLDs or www
              const isExternal =
                /^https?:\/\//i.test(ctaLink) ||
                /www\./i.test(ctaLink) ||
                /\.(com|in|net|org|co|io|gov|edu|me|info|biz|us|uk|ca|au|de|fr|jp|ru|cn|br|za|eu)([\/:]|$)/i.test(
                  ctaLink
                );

              if (isExternal) {
                return (
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{
                      borderRadius: "25px",
                      px: { xs: 2.5, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.2, md: 1.5 },
                      fontSize: {
                        xs: "0.85rem",
                        sm: "0.9rem",
                        md: "1rem",
                      },
                      fontWeight: 600,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.light
                          : theme.palette.primary.main,
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.contrastText
                          : theme.palette.primary.contrastText,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                      textTransform: "none",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                      },
                    }}
                    href={ctaLink}
                    target="_blank"
                  >
                    {item.Banner_CTA_Text}
                  </Button>
                );
              } else {
                return (
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{
                      borderRadius: "25px",
                      px: { xs: 2.5, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.2, md: 1.5 },
                      fontSize: {
                        xs: "0.85rem",
                        sm: "0.9rem",
                        md: "1rem",
                      },
                      fontWeight: 600,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.light
                          : theme.palette.primary.main,
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.contrastText
                          : theme.palette.primary.contrastText,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                      textTransform: "none",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                      },
                    }}
                    onClick={() => handleCTANavigation(ctaLink)}
                  >
                    {item.Banner_CTA_Text}
                  </Button>
                );
              }
            })()}
        </Box>
      </Box>
    );
  };

  // Fetch locations for Compact Hero Banner search bar
  const [compactLocations, setCompactLocations] = useState([]);
  const [compactLocation, setCompactLocation] = useState("");
  const [compactLoading, setCompactLoading] = useState(true);

  // Check app settings for features
  const isUserFilesEnabled = context.state?.appSettings?.find(
    (setting) =>
      setting.Setting_Name === "User Files Status" &&
      setting.Setting_Value === "Enabled"
  );

  const userFilesText =
    context.state?.appSettings?.find(
      (setting) => setting.Setting_Name === "User Files Text"
    )?.Setting_Value || "Upload Prescription";

  const isLocationSearchEnabled = context.state?.appSettings?.find(
    (setting) =>
      setting.Setting_Name === "Location Search" &&
      setting.Setting_Value === "Enabled"
  );

  useEffect(() => {
    let mounted = true;
    const fetchLocations = async () => {
      // Only fetch locations if Location Search is enabled
      if (!isLocationSearchEnabled) {
        if (mounted) setCompactLoading(false);
        return;
      }

      try {
        const response = await postRecord("locations/getall", {});
        if (response.status === 200 && response.data && mounted) {
          setCompactLocations(response.data);
          if (response.data.length > 0) {
            setCompactLocation(response.data[0].Location_Id);
          }
        }
        if (mounted) setCompactLoading(false);
      } catch (e) {
        if (mounted) setCompactLoading(false);
      }
    };
    fetchLocations();
    return () => {
      mounted = false;
    };
  }, [isLocationSearchEnabled]);

  return (
    <React.Fragment>
      {/* Full Width Hero Banners */}
      {(loading || fullWidthBanners.length > 0) && (
        <Box sx={{ position: "relative", width: "100%", mb: 6 }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={500}
              sx={{ borderRadius: "8px" }}
              animation="wave"
            />
          ) : (
            <ImageGallery
              items={fullWidthBanners.map((banner) => ({
                original: `${process.env.BASEURL}${banner.Banner_Image_File_Path}`,
              }))}
              showThumbnails={false}
              showFullscreenButton={false}
              showPlayButton={false}
              showBullets={true}
              autoPlay={true}
              slideInterval={12000}
              slideDuration={2000}
              infinite={true}
              renderItem={(item) => (
                <Box
                  sx={{
                    width: "100%",
                    height: {
                      xs: "450px",
                      sm: "600px",
                      md: "675px",
                      lg: "825px",
                      xl: "825px",
                    },
                    position: "relative",
                  }}
                >
                  <img
                    src={item.original}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                      transition: "opacity 1.2s cubic-bezier(0.4,0,0.2,1)",
                    }}
                    onLoad={() => handleImageLoad(item.original, "fullWidth")}
                  />
                  {/* Loading overlay */}
                  {!fullWidthImagesLoaded[item.original] && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(30, 30, 30, 0.9)"
                            : "rgba(245, 245, 245, 0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        zIndex: 10,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <CircularProgress
                        size={40}
                        sx={{
                          color: (theme) => theme.palette.primary.main,
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                        }}
                      />
                    </Box>
                  )}
                  {/* Overlay gradient and text section */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      bottom: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "flex-end",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                        borderRadius: "8px",
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? {
                                xs: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)",
                                md: "radial-gradient(ellipse 60% 80% at 85% 85%, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.2) 70%, transparent 100%)",
                              }
                            : {
                                xs: "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.9) 100%)",
                                md: "radial-gradient(ellipse 60% 80% at 85% 85%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 40%, rgba(255,255,255,0.3) 70%, transparent 100%)",
                              },
                        zIndex: 1,
                        transition: "background 0.3s",
                      }}
                    />
                    <Fade in={textVisible} timeout={1500}>
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 2,
                          width: {
                            xs: "100%",
                            md: "60%",
                            lg: "50%",
                            xl: "40%",
                          },
                          ml: { xs: 0, md: "auto" },
                          mb: { xs: 3, sm: 4, md: 6 },
                          p: { xs: 2, sm: 3, md: 6 },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: { xs: "center", md: "flex-end" },
                          textAlign: { xs: "center", md: "right" },
                          pointerEvents: "auto",
                          transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                        }}
                      >
                        <Typography
                          variant="h2"
                          color="primary"
                          sx={{
                            fontWeight: "bold",
                            mb: { xs: 1, md: 1 },
                            textShadow: "0 1px 4px rgba(0,0,0,0.25)",
                            transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                            fontSize: {
                              xs: "1.3rem",
                              sm: "1.8rem",
                              md: "2.7rem",
                              lg: "3.2rem",
                              xl: "3.5rem",
                            },
                            lineHeight: {
                              xs: 1.2,
                              sm: 1.3,
                              md: 1.4,
                            },
                            wordBreak: "break-word",
                            whiteSpace: {
                              xs: "normal",
                              md: "nowrap",
                            },
                            overflow: "visible",
                          }}
                        >
                          {fullWidthBanners[currentSlide]?.Image_Heading_Text}
                        </Typography>
                        <Typography
                          variant="h5"
                          color="secondary"
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: { xs: 1, md: 1 },
                            fontSize: {
                              xs: "0.9rem",
                              sm: "1.1rem",
                              md: "1.5rem",
                              lg: "1.7rem",
                              xl: "2rem",
                            },
                            lineHeight: {
                              xs: 1.1,
                              sm: 1.2,
                              md: 1.3,
                            },
                            wordBreak: "break-word",
                            whiteSpace: {
                              xs: "normal",
                              md: "nowrap",
                            },
                            overflow: "visible",
                          }}
                        >
                          {
                            fullWidthBanners[currentSlide]
                              ?.Image_Sub_Heading_Text
                          }
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: { xs: 2, md: 2 },
                            fontSize: {
                              xs: "0.85rem",
                              sm: "0.95rem",
                              md: "1.2rem",
                              lg: "1.25rem",
                              xl: "1.3rem",
                            },
                            lineHeight: {
                              xs: 1.3,
                              sm: 1.4,
                              md: 1.5,
                            },
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflow: "visible",
                            display: {
                              xs: "block",
                              md: "block",
                            },
                          }}
                        >
                          {fullWidthBanners[currentSlide]?.Image_Text}
                        </Typography>
                        {fullWidthBanners[currentSlide]?.Banner_CTA_Text &&
                          (() => {
                            const ctaLink =
                              fullWidthBanners[currentSlide]
                                ?.Banner_CTA_Button_Link || "";
                            // Check for external link: starts with http/https, or contains common TLDs or www
                            const isExternal =
                              /^https?:\/\//i.test(ctaLink) ||
                              /www\./i.test(ctaLink) ||
                              /\.(com|in|net|org|co|io|gov|edu|me|info|biz|us|uk|ca|au|de|fr|jp|ru|cn|br|za|eu)([\/:]|$)/i.test(
                                ctaLink
                              );
                            if (isExternal) {
                              return (
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{
                                    borderRadius: "25px",
                                    px: { xs: 2, md: 3 },
                                    py: { xs: 1, md: 1.5 },
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "0.875rem",
                                      md: "1rem",
                                    },
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.primary.light
                                        : theme.palette.primary.main,
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.primary.contrastText,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                    transition:
                                      "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                                    maxWidth: { xs: "200px", md: "none" },
                                    textTransform: "none",
                                  }}
                                  href={ctaLink}
                                  target="_blank"
                                >
                                  {
                                    fullWidthBanners[currentSlide]
                                      ?.Banner_CTA_Text
                                  }
                                </Button>
                              );
                            } else {
                              return (
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{
                                    borderRadius: "25px",
                                    px: { xs: 2, md: 3 },
                                    py: { xs: 1, md: 1.5 },
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "0.875rem",
                                      md: "1rem",
                                    },
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.primary.light
                                        : theme.palette.primary.main,
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.primary.contrastText,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                    transition:
                                      "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                                    maxWidth: { xs: "200px", md: "none" },
                                    textTransform: "none",
                                  }}
                                  onClick={() => handleCTANavigation(ctaLink)}
                                >
                                  {
                                    fullWidthBanners[currentSlide]
                                      ?.Banner_CTA_Text
                                  }
                                </Button>
                              );
                            }
                          })()}
                      </Box>
                    </Fade>
                  </Box>
                </Box>
              )}
              onSlide={(idx) => {
                setTextVisible(false);
                setTimeout(() => {
                  setCurrentSlide(idx);
                  setTextVisible(true);
                }, 1200); // match fade duration
              }}
              additionalClass="height-controlled-gallery"
            />
          )}
        </Box>
      )}

      {/* Compact Hero Banners as slider */}
      {(loading || compactHeroBanners.length > 0) && (
        <Box
          sx={{
            width: "100%",
            position: "relative",
            mt: { xs: 0, sm: 6, md: 8, lg: 10, xl: 10 },
            mb: 4,
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "relative", width: "100%" }}>
            {loading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={500}
                sx={{ borderRadius: "0px" }}
                animation="wave"
              />
            ) : (
              <ImageGallery
                items={compactHeroBanners}
                showThumbnails={false}
                showFullscreenButton={false}
                showPlayButton={false}
                showBullets={true}
                autoPlay={true}
                slideInterval={10000}
                slideDuration={1800}
                infinite={true}
                renderItem={renderCompactHeroBannerItem}
                additionalClass="height-controlled-gallery"
              />
            )}
          </Box>
        </Box>
      )}

      {/* Standard Hero Banners (existing design) */}
      {(loading || heroBanners.length > 0) && (
        <Container
          maxWidth="xl"
          sx={{
            paddingTop: { xs: 6, sm: 6, md: 8, lg: 10, xl: 12 }, // xs increased by 2
            marginBottom: 6,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={550}
                  sx={{ borderRadius: "8px" }}
                  animation="wave"
                />
              ) : (
                <Box sx={{ overflow: "hidden", borderRadius: "8px" }}>
                  <ImageGallery
                    items={heroBanners.map((banner) => ({
                      original: `${process.env.BASEURL}${banner.Banner_Image_File_Path}`,
                    }))}
                    showThumbnails={false}
                    showFullscreenButton={false}
                    showPlayButton={false}
                    showBullets={true}
                    autoPlay={true}
                    slideInterval={12000}
                    slideDuration={2000}
                    onSlide={(idx) => {
                      setTextVisible(false);
                      setTimeout(() => {
                        setCurrentSlide(idx);
                        setTextVisible(true);
                      }, 1200);
                    }}
                    infinite={true}
                    renderItem={renderItem}
                    additionalClass="height-controlled-gallery"
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
              {loading ? (
                <Paper
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    height: {
                      xs: "300px",
                      sm: "400px",
                      md: "450px",
                      lg: "550px",
                      xl: "550px",
                    },
                    padding: "20px",
                    borderRadius: "8px",
                  }}
                >
                  <Box sx={{ textAlign: "right" }}>
                    <Skeleton animation="wave" height={80} width="100%" />
                    <Skeleton
                      animation="wave"
                      height={60}
                      width="80%"
                      sx={{ marginLeft: "auto" }}
                    />
                    <Skeleton animation="wave" height={120} />
                    <Skeleton
                      animation="wave"
                      height={40}
                      width="40%"
                      sx={{ marginLeft: "auto" }}
                    />
                  </Box>
                </Paper>
              ) : (
                heroBanners.length > 0 &&
                currentSlide < heroBanners.length && (
                  <Fade in={textVisible} timeout={1500}>
                    <Paper
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: {
                          xs: "300px",
                          sm: "400px",
                          md: "450px",
                          lg: "550px",
                          xl: "550px",
                        },
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "right",
                        transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h1"
                          color={"primary"}
                          sx={{
                            fontWeight: "bold",
                            fontSize: {
                              xs: "1.7rem", // reduced for mobile
                              sm: "2.5rem",
                              md: "2.8rem",
                              lg: "3.2rem",
                              xl: "3.5rem",
                            },
                            transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                          }}
                        >
                          {heroBanners[currentSlide].Image_Heading_Text}
                        </Typography>
                        <Typography
                          variant="h3"
                          color="secondary"
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 600,
                            pb: 2,
                            fontSize: {
                              xs: "1.1rem", // reduced for mobile
                              sm: "1.3rem",
                              md: "1.5rem",
                              lg: "1.7rem",
                              xl: "2rem",
                            },
                          }}
                        >
                          {heroBanners[currentSlide].Image_Sub_Heading_Text}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            marginBottom: 4,
                            fontSize: {
                              xs: "0.95rem", // reduced for mobile
                              sm: "1.1rem",
                              md: "1.2rem",
                              lg: "1.25rem",
                              xl: "1.3rem",
                            },
                          }}
                        >
                          {heroBanners[currentSlide].Image_Text}
                        </Typography>{" "}
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{
                            padding: "10px 20px",
                            borderRadius: "25px",
                            alignSelf: "flex-end",
                            transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)",
                          }}
                          onClick={() =>
                            handleCTANavigation(
                              heroBanners[currentSlide].Banner_CTA_Button_Link
                            )
                          }
                        >
                          {heroBanners[currentSlide].Banner_CTA_Text}
                        </Button>
                      </Box>
                    </Paper>
                  </Fade>
                )
              )}
            </Grid>
          </Grid>
        </Container>
      )}

      {/* User Files Upload Dialog - only show if enabled */}
      {isUserFilesEnabled && (
        <UserFilesUpload
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={() => setDialogOpen(false)}
        />
      )}

      {/* Add minimal styling to ensure images fit properly */}
      <style>{`
        .height-controlled-gallery, 
        .height-controlled-gallery .image-gallery-slide,
        .height-controlled-gallery .image-gallery-swipe,
        .height-controlled-gallery .image-gallery-slides {
          height: 100%;
        }
      `}</style>
    </React.Fragment>
  );
}
